import { Controller } from "@hotwired/stimulus"

// Share popover for a project — wraps POST/PATCH/DELETE on
// /projects/:id/share and shows the public URL with copy/regenerate/revoke.
//
// Can mount in two contexts:
//   1. Editor header (`/projects/:id/edit` and `/p/:token` is NOT this controller).
//   2. `/projects` list, per-row — a compact toggle that opens the popover.
//
// Required data attributes on the controller element:
//   data-share-project-id-value  → the owning Project#id
//   data-share-shared-value      → "true" / "false" at mount
//   data-share-url-value         → the full /p/<token> URL, if already shared
//
// Targets: `popover` (the floating panel), `urlInput`, `toggle` (button that
// opens/closes the popover), `status` (text slot for errors).
export default class extends Controller {
  static targets = ["popover", "urlInput", "toggle", "status", "enableBlock", "sharedBlock"]
  static values = {
    projectId: String,
    shared: { type: Boolean, default: false },
    url: { type: String, default: "" },
  }

  connect() {
    this._onDocClick = (e) => {
      if (!this.hasPopoverTarget) return
      if (this.popoverTarget.classList.contains("hidden")) return
      if (this.element.contains(e.target)) return
      this.#closePopover()
    }
    document.addEventListener("click", this._onDocClick)
    this.#renderState()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
  }

  togglePopover(e) {
    e?.stopPropagation()
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) this.#openPopover()
    else this.#closePopover()
  }

  async enable() {
    await this.#call("POST")
  }

  async regenerate() {
    if (!confirm("Создать новую ссылку? Старая перестанет работать.")) return
    await this.#call("PATCH")
  }

  async revoke() {
    if (!confirm("Отключить общий доступ? Ссылка перестанет работать.")) return
    await this.#call("DELETE")
  }

  async copy() {
    if (!this.urlValue) return
    try {
      await navigator.clipboard.writeText(this.urlValue)
      this.#setStatus("Скопировано", "success")
      setTimeout(() => this.#setStatus(""), 1500)
    } catch (e) {
      // Fallback: select the input so the user can hit ⌘C manually.
      if (this.hasUrlInputTarget) {
        this.urlInputTarget.select()
        this.urlInputTarget.setSelectionRange(0, 99999)
      }
      this.#setStatus("Выделено — нажмите ⌘C", "warn")
    }
  }

  async #call(method) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(`/projects/${this.projectIdValue}/share`, {
        method,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (data.shared === false) {
        this.sharedValue = false
        this.urlValue = ""
      } else {
        this.sharedValue = true
        this.urlValue = data.public_url || ""
      }
      this.#renderState()
      this.#setStatus("")
    } catch (e) {
      this.#setStatus("Ошибка — попробуйте ещё раз", "error")
    }
  }

  #openPopover() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.remove("hidden")
    // Focus the URL input on open for easy copy.
    if (this.sharedValue && this.hasUrlInputTarget) {
      queueMicrotask(() => {
        this.urlInputTarget.focus()
        this.urlInputTarget.select()
      })
    }
  }

  #closePopover() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.add("hidden")
    this.#setStatus("")
  }

  #renderState() {
    if (this.hasUrlInputTarget) this.urlInputTarget.value = this.urlValue
    if (this.hasEnableBlockTarget) this.enableBlockTarget.classList.toggle("hidden", this.sharedValue)
    if (this.hasSharedBlockTarget) this.sharedBlockTarget.classList.toggle("hidden", !this.sharedValue)
    if (this.hasToggleTarget) {
      // Mirror current state in the trigger button (visual only).
      this.toggleTarget.dataset.shareActive = this.sharedValue ? "true" : "false"
    }
  }

  #setStatus(text, tone = "info") {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.dataset.tone = tone
  }
}
